// BUILD-046 task 8.15 (supporting model): the two asynchronous flows of the Assign task dialog, kept out of the component so the ORDER of
// state changes is tested (design "Assign task dialog"; Req 3.1 - 3.3, 3.10 - 3.15, 3.18, 3.20).
//
//   runPreview : asks the server who would receive the task for the CURRENT selection (once per distinct selection), and records the answer
//                against that selection. A preview that fails blocks nothing; an answer for an earlier selection is ignored by the model.
//   runSubmit  : the submit handler. A pending or finished submission sends nothing; a submission with a missing title, recipient or mode is
//                refused with a message naming each missing field; otherwise ONE request is sent with the dialog's submission id. On failure
//                every entered value stays and the same id is kept (a retry cannot create a second task); on success a new id is taken.
//
// `store` is the component's state holder: `get` always returns the latest state (a ref) and `set` publishes the next one.
// Pure of React and of the network (the api is passed in). Erasable TypeScript; runtime imports are the assign model only.

import { attemptSubmit, previewFailed, previewLoaded, previewRequest, previewStarted, submitFailed, submitSucceeded } from './assignModel';
import type { AssignState, PreviewData } from './assignModel';
import type { AssignRequest } from '../../api/workShared';

export interface AssignFlowApi<R extends { requestId: string }> {
  previewAssign: (request: AssignRequest) => Promise<PreviewData>;
  assign: (request: AssignRequest) => Promise<R>;
}

export interface AssignStore {
  get: () => AssignState;
  set: (next: AssignState) => void;
}

/** Sends the preview for the current selection when one is needed. Returns whether a request was sent. */
export async function runPreview<R extends { requestId: string }>(api: AssignFlowApi<R>, store: AssignStore): Promise<boolean> {
  const wanted = previewRequest(store.get());
  if (wanted === null) return false;
  store.set(previewStarted(store.get(), wanted.key));
  try {
    const data = await api.previewAssign(wanted.request);
    store.set(previewLoaded(store.get(), wanted.key, data));
  } catch (thrown) {
    store.set(previewFailed(store.get(), wanted.key, thrown));
  }
  return true;
}

export type SubmitOutcome<R> = { sent: false } | { sent: true; ok: true; response: R; title: string } | { sent: true; ok: false; error: unknown };

/**
 * The submit handler. `newId` supplies a fresh submission id (a UUID in the browser). The outcome carries the accepted response and the
 * title as submitted, so the host can refresh its lists even if the dialog was closed while the request was running.
 */
export async function runSubmit<R extends { requestId: string }>(api: AssignFlowApi<R>, store: AssignStore, newId: () => string): Promise<SubmitOutcome<R>> {
  const step = attemptSubmit(store.get());
  store.set(step.state);
  if (step.request === null) return { sent: false };
  const title = step.request.title ?? '';
  try {
    const response = await api.assign(step.request);
    store.set(submitSucceeded(store.get(), response, newId()));
    return { sent: true, ok: true, response, title };
  } catch (thrown) {
    store.set(submitFailed(store.get(), thrown, newId()));
    return { sent: true, ok: false, error: thrown };
  }
}
